import { useNavigate } from '@tanstack/react-router';
import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Kbd } from '../design/ui';
import { NAV, type NavKey } from './nav';

/**
 * «Знайти розділ» (ref. ⌘K): every section of the role by its name, keys ↑ ↓ and Enter. So the
 * menu can stay short and nothing gets lost.
 */
export function CommandPalette({ sections, onClose }: { sections: NavKey[]; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const found = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('uk');
    return sections.filter((k) => !q || t(`nav.${k}`).toLocaleLowerCase('uk').includes(q));
  }, [query, sections, t]);

  useEffect(() => input.current?.focus(), []);
  useEffect(() => setActive(0), [query]);

  const go = (key: NavKey | undefined) => {
    if (!key) return;
    onClose();
    void navigate({ to: NAV[key].to });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[rgb(23_23_27/0.18)] px-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={t('shell.search')}
        className="w-full max-w-[520px] overflow-hidden rounded-[20px] border border-stroke bg-white shadow-float"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(found.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter') go(found[active]);
        }}
      >
        <label className="flex h-14 items-center gap-3 border-b border-stroke px-4">
          <Search size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
          <input
            ref={input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('shell.searchPlaceholder')}
            className="h-full flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-faint"
          />
          <Kbd>Esc</Kbd>
        </label>
        <ul className="max-h-[52vh] overflow-y-auto p-2">
          {found.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted">{t('shell.nothingFound')}</li>
          )}
          {found.map((key, i) => {
            const Icon = NAV[key].icon;
            return (
              <li key={key}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(key)}
                  className={`flex h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium ${i === active ? 'bg-fill text-ink' : 'text-ink-soft'}`}
                >
                  <Icon size={17} strokeWidth={1.7} className="text-muted" aria-hidden />
                  <span className="flex-1">{t(`nav.${key}`)}</span>
                  {i === active && <CornerDownLeft size={15} className="text-faint" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
